import clsx from 'clsx';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LOADING_STATUS } from '../../shared/constants/loadingStatus';
import { getTemplateEditRoute } from '../../shared/config/routes';
import { useFromLocationState } from '../../shared/hooks';
import { useTenantSettingsLoader } from '../../features/templates/hooks/useTenantSettingsLoader';
import { useTenantLanguages } from '../../features/templates/hooks/useTenantLanguages';
import {
  selectTemplatesTenantVariablesSyncedAt,
  selectTemplatesTenantSettingsStatus,
} from '../../features/templates/store/templatesSlice';
import { useTemplatePreview } from '../../features/templates/hooks/useTemplatePreview';
import { TemplatePreviewPanel } from '../../features/templates/ui';
import {
  selectCampaignCadence,
  selectCampaignCadenceStatus,
  selectCampaignCadencesCampaignsStatus,
  selectCampaignCadencesSelectedTypeTab,
  selectCampaignCadenceReorderStatus,
  campaignCadencesCampaignSelected,
  campaignCadencesTypeTabChanged,
  campaignCadencesChannelChanged,
} from '../../features/campaign-cadences/store/campaignCadencesSlice';
import { CAMPAIGN_CADENCES_TYPE_TAB_KEY } from '../../features/campaign-cadences/constants/campaignCadencesTabs';
import { useCampaignCadencesData } from '../../features/campaign-cadences/hooks/useCampaignCadencesData';
import { formatDateTimeLabel } from '../../shared/lib/formatDateTimeLabel';
import { useCampaignCadencesEditing } from '../../features/campaign-cadences/hooks/useCampaignCadencesEditing';
import { usePermissions } from '../../features/auth/hooks/usePermissions';
import { DiscardChangesDialog } from '../../features/templates/ui/create-template/discard-changes-dialog/DiscardChangesDialog';
import { SpinnerWrap } from '../../shared/ui/spinner/Spinner';
import { CampaignCadencesSubheader } from './ui/subheader/CampaignCadencesSubheader';
import { StickyCampaignsSidebar } from './ui/campaigns-sidebar/StickyCampaignsSidebar';
import { CampaignCadencesChannelTabs } from './ui/channel-tabs/CampaignCadencesChannelTabs';
import { CampaignCadencesList } from './ui/campaign-cadences-list/CampaignCadencesList';
import layoutStyles from '../../shared/styles/layout.module.css';
import styles from './CampaignCadencesPage.module.css';

export function CampaignCadencesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fromState = useFromLocationState();

  const campaignsStatus = useSelector(selectCampaignCadencesCampaignsStatus);
  const selectedTypeTab = useSelector(selectCampaignCadencesSelectedTypeTab);
  const campaignCadence = useSelector(selectCampaignCadence);
  const campaignCadenceStatus = useSelector(selectCampaignCadenceStatus);
  const reorderStatus = useSelector(selectCampaignCadenceReorderStatus);
  const variablesSyncedAt = useSelector(selectTemplatesTenantVariablesSyncedAt);
  const tenantSettingsStatus = useSelector(selectTemplatesTenantSettingsStatus);

  const isTriggersTab = selectedTypeTab === CAMPAIGN_CADENCES_TYPE_TAB_KEY.TRIGGERS;
  const isSaving = reorderStatus === LOADING_STATUS.LOADING;

  useCampaignCadencesData();
  useTenantSettingsLoader();

  const { canEdit } = usePermissions();
  const { previewTemplate, openPreview, closePreview } = useTemplatePreview();

  const { languages } = useTenantLanguages();

  const {
    isEditing,
    savedSteps,
    localSteps,
    setLocalSteps,
    hasChanges,
    discardOpen,
    showSaveOnDiscard,
    guardAction,
    handleEdit,
    handleSave,
    handleDiscardConfirm,
    handleDiscardClose,
    handleSaveAndProceed,
  } = useCampaignCadencesEditing(campaignCadence, languages);

  const items = campaignCadence?.items ?? [];
  const displaySteps = isEditing ? localSteps : savedSteps;
  const hasTemplates = (isTriggersTab ? items.length : displaySteps.length) > 0;
  const updatedLabel = formatDateTimeLabel(variablesSyncedAt);

  const isTenantSettingsResolved =
    tenantSettingsStatus === LOADING_STATUS.SUCCEEDED ||
    tenantSettingsStatus === LOADING_STATUS.FAILED;
  const isContentReady = campaignsStatus === LOADING_STATUS.SUCCEEDED && isTenantSettingsResolved;

  const handleEditTemplate = useCallback(
    (template) => {
      navigate(getTemplateEditRoute(template.channel, template.id), { state: fromState });
    },
    [navigate, fromState],
  );

  const handleDiscard = useCallback(() => {
    guardAction(() => {}, false);
  }, [guardAction]);

  const handleBack = useCallback(() => {
    guardAction(() => navigate(-1), true);
  }, [guardAction, navigate]);

  const handleTypeTabChange = useCallback(
    (key) => {
      guardAction(() => dispatch(campaignCadencesTypeTabChanged(key)), true);
    },
    [guardAction, dispatch],
  );

  const handleCampaignSelect = useCallback(
    (id) => {
      guardAction(() => dispatch(campaignCadencesCampaignSelected(id)), true);
    },
    [guardAction, dispatch],
  );

  const handleChannelChange = useCallback(
    (key) => {
      guardAction(() => dispatch(campaignCadencesChannelChanged(key)), true);
    },
    [guardAction, dispatch],
  );

  return (
    <>
      <CampaignCadencesSubheader
        isEditing={isEditing}
        isSaving={isSaving}
        hasChanges={hasChanges}
        isEmpty={!hasTemplates}
        canEdit={canEdit}
        onEdit={handleEdit}
        onDiscard={handleDiscard}
        onSave={handleSave}
        onBack={handleBack}
        onTypeTabChange={handleTypeTabChange}
      />
      <div className={clsx(layoutStyles.pageContentWrap)}>
        {!isContentReady ? (
          <SpinnerWrap ariaLabel="Loading campaign cadences" />
        ) : (
          <div className={clsx(styles.layout, isTriggersTab && styles.layoutNoSidebar)}>
            {isTriggersTab ? null : <StickyCampaignsSidebar onSelect={handleCampaignSelect} />}
            <section className={clsx(styles.content)}>
              <div className={clsx(styles.tabsRow)}>
                {updatedLabel && hasTemplates ? (
                  <p className={clsx(styles.updated)}>Last updated: {updatedLabel}</p>
                ) : null}
                <CampaignCadencesChannelTabs onBeforeChange={handleChannelChange} />
              </div>
              {campaignCadenceStatus !== LOADING_STATUS.SUCCEEDED ? (
                <SpinnerWrap ariaLabel="Loading campaign cadence" />
              ) : (
                <CampaignCadencesList
                  steps={displaySteps}
                  items={items}
                  languages={languages}
                  isEditing={isEditing}
                  variant={selectedTypeTab}
                  onStepsChange={setLocalSteps}
                  onPreview={openPreview}
                  onEdit={canEdit ? handleEditTemplate : undefined}
                />
              )}
            </section>
          </div>
        )}
      </div>
      <DiscardChangesDialog
        open={discardOpen}
        onClose={handleDiscardClose}
        onDiscard={handleDiscardConfirm}
        onSave={showSaveOnDiscard ? handleSaveAndProceed : undefined}
      />
      <TemplatePreviewPanel
        open={previewTemplate !== null}
        onClose={closePreview}
        template={previewTemplate}
      />
    </>
  );
}
